/** The account band's cells (plan "Page anatomy" → Account band): who is this and can
 *  they get in — facts, not big numbers, each in a title, a datum line and a muted detail
 *  line, and each linked to the tab that owns it. Pure: `now` is a parameter.
 *
 *  | Cell    | system                                 | staff                         | guest                       |
 *  |---------|----------------------------------------|-------------------------------|-----------------------------|
 *  | Account | Active / Inactive / Deleted · last login or "never signed in"                                        |
 *  | Sign-in | MFA method or "MFA not enrolled" · sessions · invite pending | MFA · sessions | magic link → date / none · sessions |
 *  | Role    | groups · N console perms · superuser   | roles @ tenant · access groups | Guest @ tenant · access groups |
 *  | Origin  | joined                                 | joined                        | OTA · joined                |
 *
 *  The Trips cell comes from the Trips panel's queries (`tripsBandCell`). Times are UTC
 *  (a person spans tours; the tooltip names the zone). */
import type { ConsoleUserDetail, MFADevice } from '@/api/users';
import { formatZoned } from '@/utils/zonedTime';

/** The tabs a band cell can lead to. */
export type BandTab = 'sessions' | 'authentication' | 'permissions';

export interface BandCell {
  id: 'account' | 'signin' | 'role' | 'trips' | 'origin';
  title: string;
  datum: string;
  detail: string | null;
  /** The tab that owns the cell's facts. */
  tab?: BandTab;
  /** The datum's instant in full (UTC), for its tooltip. */
  tooltip?: string;
}

const MFA_KIND: Record<MFADevice['kind'], string> = { totp: 'TOTP', sms: 'SMS', email: 'Email' };

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "2 Oct" (the year when it is not this year), UTC. */
export function bandDay(iso: string, now: Date): string {
  return formatZoned(iso, 'UTC', { weekday: false, time: false, zone: false, now });
}

/** "Fri 2 Oct 09:30 UTC" — a band datum's tooltip. */
export function bandInstant(iso: string, now: Date): string {
  return formatZoned(iso, 'UTC', { now, year: 'always' });
}

const monthYearFormat = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

/** "Aug 2022". */
export function monthYear(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : monthYearFormat.format(d);
}

/** "a, b +3" — the first `max` names, then how many more. Each is trimmed: a stored name
 *  can end in a space ("Full access , Some guides"). */
export function namesList(names: string[], max = 2): string {
  const shown = names.slice(0, max).map((n) => n.trim()).join(', ');
  return names.length <= max ? shown : `${shown} +${names.length - max}`;
}

function accountCell(u: ConsoleUserDetail, now: Date): BandCell {
  return {
    id: 'account',
    title: 'Account',
    datum: u.is_deleted ? 'Deleted' : u.is_active ? 'Active' : 'Inactive',
    detail: u.last_login ? `last login ${bandDay(u.last_login, now)}` : 'never signed in',
    tab: 'sessions',
    ...(u.last_login ? { tooltip: `Last login ${bandInstant(u.last_login, now)}` } : {}),
  };
}

/** The MFA line: the confirmed methods, in words. */
export function mfaText(u: Pick<ConsoleUserDetail, 'mfa_enrolled' | 'devices'>): string {
  if (!u.mfa_enrolled) return 'MFA not enrolled';
  const kinds = [...new Set(u.devices.filter((d) => d.confirmed).map((d) => MFA_KIND[d.kind]))];
  return kinds.length ? `MFA · ${kinds.join(', ')}` : 'MFA on';
}

/** A guest's sign-in: the magic link and until when it is valid. */
export function magicLinkText(u: Pick<ConsoleUserDetail, 'magic_link'>, now: Date): string {
  const m = u.magic_link;
  if (!m?.present) return 'No magic link';
  if (!m.expires) return 'Magic link, no expiry';
  return new Date(m.expires).getTime() <= now.getTime()
    ? `Magic link expired ${bandDay(m.expires, now)}`
    : `Magic link valid → ${bandDay(m.expires, now)}`;
}

function signInCell(u: ConsoleUserDetail, now: Date): BandCell {
  const sessions = u.active_tokens_count > 0 ? plural(u.active_tokens_count, 'session') : 'no sessions';
  if (u.kind === 'guest') {
    return {
      id: 'signin',
      title: 'Sign-in',
      datum: magicLinkText(u, now),
      detail: sessions,
      tab: 'sessions',
      ...(u.magic_link?.expires ? { tooltip: `Expires ${bandInstant(u.magic_link.expires, now)}` } : {}),
    };
  }
  const invite = u.kind === 'system' && (u.pending_invite_count ?? 0) > 0 ? 'invite pending' : null;
  return {
    id: 'signin',
    title: 'Sign-in',
    datum: mfaText(u),
    detail: [sessions, invite].filter(Boolean).join(' · '),
    tab: 'authentication',
  };
}

const tenantWord = (t: { name: string; code: string }) => t.code || t.name;

function roleCell(u: ConsoleUserDetail): BandCell {
  const groups = u.access_groups.map((g) => g.name);
  if (u.kind === 'system') {
    const perms = plural(u.permissions_count ?? 0, 'console perm');
    return {
      id: 'role',
      title: 'Role',
      datum: u.groups.length ? namesList(u.groups.map((g) => g.name)) : 'No console groups',
      detail: [perms, u.is_superuser ? 'superuser' : null].filter(Boolean).join(' · '),
      tab: 'permissions',
    };
  }
  if (u.kind === 'staff') {
    const roles = u.tour_operator_users.map(
      (t) => `${t.roles.map((r) => r.name.trim()).join(', ') || 'Staff'} @ ${tenantWord(t.tour_operator)}`,
    );
    return {
      id: 'role',
      title: 'Role',
      datum: roles.length ? namesList(roles, 1) : 'Staff',
      detail: groups.length ? namesList(groups) : 'no access groups',
      tab: 'permissions',
    };
  }
  return {
    id: 'role',
    title: 'Role',
    datum: u.tour_operator ? `Guest @ ${tenantWord(u.tour_operator)}` : 'Guest',
    detail: groups.length ? namesList(groups) : null,
    tab: 'permissions',
  };
}

function originCell(u: ConsoleUserDetail): BandCell {
  const joined = u.date_joined ? `joined ${monthYear(u.date_joined)}` : null;
  const ota = u.kind === 'guest' ? u.attributes.ota?.name : null;
  if (ota) return { id: 'origin', title: 'Origin', datum: ota, detail: ['OTA', joined].filter(Boolean).join(' · ') };
  return {
    id: 'origin',
    title: 'Origin',
    datum: u.date_joined ? `Joined ${monthYear(u.date_joined)}` : 'Join date unknown',
    detail: null,
  };
}

/** The band's cells, in order; `trips` (the Trips panel's summary) goes before Origin
 *  when given. */
export function accountBandCells(u: ConsoleUserDetail, now: Date = new Date(), trips?: BandCell | null): BandCell[] {
  return [accountCell(u, now), signInCell(u, now), roleCell(u), ...(trips ? [trips] : []), originCell(u)];
}
